import React, { useCallback } from 'react';
import { FoldedSearchHighlight } from './FoldedSearchHighlight';
import IntegerInput from './IntegerInput';
import { getParcaFotoUrl } from '../services/productsService';
import {
  useSiparisParcaCatalog,
  DRAFT_CAT,
  getCategoryId,
  getContentName,
  getContentCode,
} from '../hooks/useSiparisParcaCatalog';
import './SiparisParcaPicker.css';

const ParcaThumb = ({ parca }) => {
  const src = getParcaFotoUrl(parca?.fotoUrl);
  if (src) {
    return (
      <img
        src={src}
        alt=""
        className="siparis-parca-picker__thumb"
        loading="lazy"
      />
    );
  }
  return (
    <span className="siparis-parca-picker__thumb-placeholder" title="Görsel yok">
      <i className="fas fa-image" aria-hidden />
    </span>
  );
};

/**
 * TP/AP sipariş onay ve nested picker: kategori akordiyonu + parça arama + parça listesi
 */
const SiparisParcaPicker = ({
  showPrice = true,
  onAddPart,
  birimFiyatFromParca,
  secilenParcalar = [],
  onRemovePart,
  showSelectedSummary = true,
  eslesenUrunIcerikId = null,
}) => {
  const {
    categories,
    loading,
    selectedCategoryId,
    setSelectedCategoryId,
    expandedIds,
    toggleExpand,
    selectCategory,
    categoryTree,
    parcaArama,
    setParcaArama,
    parcaAramaDebounced,
    qtyById,
    setQtyById,
    filteredParcalar,
  } = useSiparisParcaCatalog({
    secilenParcalar,
    enableDraftFilter: true,
    eslesenUrunIcerikId,
  });

  const renderCategoryNode = (node, depth = 0) => {
    const cid = node.cid;
    const hasChildren = (node.children || []).length > 0;
    const isExpanded = expandedIds[cid] === true;
    const isSelected = selectedCategoryId === cid;

    return (
      <div key={cid} className="siparis-parca-picker__cat-node" style={{ paddingLeft: depth * 10 }}>
        <div
          className={`siparis-parca-picker__cat-row ${isSelected ? 'is-selected' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => selectCategory(cid)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              selectCategory(cid);
            }
          }}
        >
          {hasChildren ? (
            <button
              type="button"
              className="siparis-parca-picker__cat-toggle"
              onClick={(e) => toggleExpand(cid, e)}
              aria-label={isExpanded ? 'Daralt' : 'Genişlet'}
            >
              <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`} />
            </button>
          ) : (
            <span className="siparis-parca-picker__cat-toggle-spacer" />
          )}
          <span className="siparis-parca-picker__cat-name">{node.kategoriAdi || '—'}</span>
        </div>
        {hasChildren && isExpanded && (
          <div className="siparis-parca-picker__cat-children">
            {node.children.map((ch) => renderCategoryNode(ch, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const handleAdd = useCallback(
    (parca) => {
      const id = String(parca.id || parca._id);
      const adet = Math.max(1, parseInt(qtyById[id], 10) || 1);
      onAddPart?.(parca, adet);
    },
    [qtyById, onAddPart],
  );

  if (loading) {
    return (
      <div className="siparis-parca-picker__loading">
        <i className="fas fa-spinner fa-spin" /> Parçalar yükleniyor...
      </div>
    );
  }

  return (
    <div className="siparis-parca-picker">
      {showSelectedSummary && secilenParcalar.length > 0 && (
        <div className="siparis-parca-picker__selected-bar">
          <span>
            <strong>{secilenParcalar.length}</strong> parça seçildi
          </span>
          <div className="siparis-parca-picker__selected-chips">
            {secilenParcalar.map((p) => {
              const id = p.icerikId || p.id;
              return (
                <span key={id} className="siparis-parca-picker__chip">
                  {getContentName(p)} ×{p.adet || 1}
                  {onRemovePart ? (
                    <button
                      type="button"
                      onClick={() => onRemovePart(id)}
                      aria-label="Kaldır"
                    >
                      <i className="fas fa-times" />
                    </button>
                  ) : null}
                </span>
              );
            })}
          </div>
        </div>
      )}
      <div className="siparis-parca-picker__search-wrap">
        <i className="fas fa-search search-icon" />
        <input
          type="text"
          className="siparis-parca-picker__search-input"
          placeholder="Parça adı veya kod ile ara..."
          value={parcaArama}
          onChange={(e) => setParcaArama(e.target.value)}
          maxLength={100}
        />
      </div>

      <div className="siparis-parca-picker__categories-panel">
        <div className="siparis-parca-picker__cat-header">Kategoriler</div>
        <div className="siparis-parca-picker__cat-toolbar">
          <button
            type="button"
            className={`siparis-parca-picker__cat-all ${!selectedCategoryId ? 'is-selected' : ''}`}
            onClick={() => setSelectedCategoryId(null)}
          >
            <i className="fas fa-layer-group" />
            Tüm parçalar
          </button>
          {secilenParcalar?.length > 0 ? (
            <button
              type="button"
              className={`siparis-parca-picker__cat-all ${selectedCategoryId === DRAFT_CAT ? 'is-selected' : ''}`}
              onClick={() => setSelectedCategoryId(DRAFT_CAT)}
            >
              <i className="fas fa-file-alt" />
              Taslaklar ({secilenParcalar.length})
            </button>
          ) : null}
        </div>
        <div className="siparis-parca-picker__cat-tree">
          {categoryTree.map((node) => renderCategoryNode(node))}
        </div>
      </div>

      <div className="siparis-parca-picker__parts">
        <div className="siparis-parca-picker__parts-header">
          <span>
            {selectedCategoryId === DRAFT_CAT
              ? 'Taslaklar'
              : selectedCategoryId
                ? categories.find((c) => getCategoryId(c) === selectedCategoryId)?.kategoriAdi ||
                  'Kategori'
                : 'Tüm parçalar'}
          </span>
          <span className="siparis-parca-picker__parts-count">{filteredParcalar.length} kayıt</span>
        </div>
        <div className="siparis-parca-picker__table-wrap">
          {filteredParcalar.length === 0 ? (
            <div className="siparis-parca-picker__empty">Parça bulunamadı</div>
          ) : (
            <table
              className={`admins-table siparis-parca-picker__table siparis-parca-picker__table--with-thumb${
                showPrice ? ' siparis-parca-picker__table--with-price' : ' siparis-parca-picker__table--no-price'
              }`}
            >
              <colgroup>
                <col className="siparis-parca-picker__col-thumb" />
                <col />
                <col />
                {showPrice ? <col /> : null}
                <col />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th aria-label="Görsel" />
                  <th>Parça</th>
                  <th>Kod</th>
                  {showPrice && <th style={{ textAlign: 'right' }}>Fiyat</th>}
                  <th style={{ textAlign: 'center' }}>Adet</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filteredParcalar.map((p) => {
                  const pid = String(p.id || p._id);
                  const birim = showPrice && birimFiyatFromParca ? birimFiyatFromParca(p) : null;
                  return (
                    <tr key={pid}>
                      <td className="siparis-parca-picker__cell-thumb">
                        <ParcaThumb parca={p} />
                      </td>
                      <td title={getContentName(p)}>
                        <FoldedSearchHighlight text={getContentName(p)} queryRaw={parcaAramaDebounced} />
                      </td>
                      <td title={getContentCode(p)}>
                        <FoldedSearchHighlight text={getContentCode(p)} queryRaw={parcaAramaDebounced} />
                      </td>
                      {showPrice && (
                        <td style={{ textAlign: 'right' }}>
                          {birim != null ? `${birim.toFixed(2)} ₺` : '—'}
                        </td>
                      )}
                      <td style={{ textAlign: 'center' }}>
                        <IntegerInput
                          min={1}
                          className="siparis-parca-picker__qty"
                          value={qtyById[pid] ?? 1}
                          onChange={(v) => setQtyById((prev) => ({ ...prev, [pid]: v }))}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="category-action-btn siparis-parca-picker__add-btn"
                          onClick={() => handleAdd(p)}
                          title="Ekle"
                        >
                          <i className="fas fa-plus" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default SiparisParcaPicker;
